import { Component, forwardRef, input, output, signal } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatTooltip } from '@angular/material/tooltip';
import { TranslatePipe } from '@ngx-translate/core';
import { DEFAULT_PERSON_IMAGE } from '../../consts/default-images.const';
import { AuthSrcDirective } from '../../directives/auth-src.directive';
import { TruncateDirective } from '../../directives/truncate.directive';
import { UiFilterChipComponent } from '../ui-filter-chip/ui-filter-chip.component';
import { PreloaderColor, UiPreloaderComponent } from '../ui-preloader/ui-preloader.component';

@Component({
  selector: 'ui-person-tile',
  imports: [
    MatCheckbox,
    AuthSrcDirective,
    UiFilterChipComponent,
    UiPreloaderComponent,
    TranslatePipe,
    TruncateDirective,
    MatTooltip,
  ],
  templateUrl: './ui-person-tile.component.html',
  styleUrl: './ui-person-tile.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => UiPersonTileComponent),
      multi: true,
    },
  ],
  host: {
    '[class.disabled]': 'isDisabled()',
    '[class.confirming]': 'isConfirming()',
  }
})
export class UiPersonTileComponent implements ControlValueAccessor {
  protected readonly preloaderColor = PreloaderColor;
  protected readonly defaultPersonImage: string = DEFAULT_PERSON_IMAGE;

  public readonly label = input.required<string>();
  public readonly imageUrl = input.required<string>();
  public readonly imageUrlFallback = input.required<string>();
  public readonly showConfirmationStatus = input.required<boolean>();
  public readonly isConfirmed = input<boolean>(false);
  public readonly isConfirming = input<boolean>(false);
  public readonly showCheckbox = input<boolean>(true);
  public readonly confirm = output<void>();
  public readonly imageLoaded = output<void>();
  public readonly imageError = output<void>();

  public onChange?: (val: boolean) => void;
  public onTouched?: () => void;

  protected value = signal<boolean>(false);
  // Show 'In group' label instead of checkbox when tile is disabled
  protected isDisabled = signal<boolean>(false);

  public writeValue(obj: boolean): void {
    this.value.set(obj);
  }

  public registerOnChange(fn: (val: boolean) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.isDisabled.set(isDisabled);
  }

  protected onCheckboxChange(checked: boolean): void {
    this.value.set(checked);
    this.onChange?.(checked);
    this.onTouched?.();
  }
}
